import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
  Box,
  Display,
  DisplayPosition,
  FlexDirection,
  Heading5,
  ZIndex,
} from '@gousto-internal/citrus-react'

import type { CollectionUUID, RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { Tooltip as UnavailableTooltip } from '../RecipeOptionsIngredients/Tooltip'

import { PillLabel, SelectablePill, SelectedPill } from './OptionPill'

export type ExperienceIngredientSlotValue = {
  recipeId?: RecipeUUID
  name: string
  isAvailable: boolean
}

export type ExperienceIngredientSlot = {
  text: string
  values: ExperienceIngredientSlotValue[]
}

type ExperienceOption = {
  recipe: {
    id: RecipeUUID
  }
  isOutOfStock: boolean
}

type TrackSelectRecipeCustomisation = {
  action: 'select_recipe_customisation'
  properties: {
    menu_uuid: string | null
    recipe_uuid?: RecipeUUID
    recipe_variant_uuid?: RecipeUUID
    event_screen: string
    customisations_selected?: Record<string, string>
  }
}

type TrackDiscloseRecipeVariants = {
  action: 'disclose_recipe_variants'
  properties: {
    recipe_uuid: RecipeUUID
    menu_uuid: string | null
    collection_id?: CollectionUUID
    event_screen: string
    customisations_displayed: Record<string, string[]>
    recipe_variants_uuids_available: RecipeUUID[]
  }
}

type Props = {
  menuId: string | null
  currentCollectionId?: CollectionUUID
  view: string
  recipeUuid: RecipeUUID
  selectedRecipeId: RecipeUUID
  options: ExperienceOption[] | null
  ingredientSlots: ExperienceIngredientSlot[]
  onSelectRecipe: (id: RecipeUUID) => void
  closeCustomisationEducation?: () => void
}

export const ExperienceIngredientOptions = ({
  menuId,
  currentCollectionId,
  view,
  recipeUuid,
  selectedRecipeId,
  options,
  ingredientSlots,
  onSelectRecipe,
  closeCustomisationEducation,
}: Props) => {
  const [tooltipKey, setTooltipKey] = useState<string | null>(null)
  const hasTrackedDisclosure = useRef(false)

  const optionByRecipeId = useMemo(
    () => new Map((options || []).map((option) => [option.recipe.id, option])),
    [options],
  )

  useEffect(() => {
    if (hasTrackedDisclosure.current) {
      return
    }

    queueTrackingEvent<TrackDiscloseRecipeVariants>({
      action: 'disclose_recipe_variants',
      properties: {
        recipe_uuid: recipeUuid,
        menu_uuid: menuId,
        collection_id: currentCollectionId,
        event_screen: view,
        customisations_displayed: ingredientSlots.reduce<Record<string, string[]>>((acc, slot) => {
          return {
            ...acc,
            [slot.text]: slot.values.map(({ name }) => name),
          }
        }, {}),
        recipe_variants_uuids_available: (options || []).map(({ recipe }) => recipe.id),
      },
    })
    hasTrackedDisclosure.current = true
  }, [currentCollectionId, ingredientSlots, menuId, options, recipeUuid, view])

  return (
    <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={5} paddingBottom={4}>
      {ingredientSlots.map((slot) => (
        <Box key={slot.text}>
          <Heading5>{slot.text}</Heading5>
          <Box display={Display.Flex} gap={3} paddingV={2} wrap>
            {slot.values.map((value) => {
              const isSelected = value.recipeId === selectedRecipeId
              const nextOption = value.recipeId ? optionByRecipeId.get(value.recipeId) : undefined
              const isUnavailable = !nextOption
              const isSoldOut =
                !isUnavailable && (!value.isAvailable || Boolean(nextOption?.isOutOfStock))

              if (isSelected) {
                return (
                  <SelectedPill key={`${slot.text}-${value.name}`}>
                    <PillLabel text={value.name} />
                  </SelectedPill>
                )
              }

              const valueKey = `${slot.text}-${value.name}`

              return (
                <Box key={valueKey} position={DisplayPosition.Relative}>
                  <SelectablePill
                    onClick={() => {
                      if (isUnavailable) {
                        setTooltipKey(valueKey)
                        return
                      }

                      const customisationsSelected = ingredientSlots.reduce<Record<string, string>>(
                        (acc, ingredientSlot) => {
                          const selectedValue =
                            ingredientSlot.text === slot.text
                              ? value
                              : ingredientSlot.values.find(
                                  (slotValue) => slotValue.recipeId === selectedRecipeId,
                                )

                          if (!selectedValue) {
                            return acc
                          }

                          return {
                            ...acc,
                            [ingredientSlot.text]: selectedValue.name,
                          }
                        },
                        {},
                      )

                      closeCustomisationEducation?.()
                      queueTrackingEvent<TrackSelectRecipeCustomisation>({
                        action: 'select_recipe_customisation',
                        properties: {
                          menu_uuid: menuId,
                          recipe_uuid: recipeUuid,
                          recipe_variant_uuid: value.recipeId,
                          event_screen: view,
                          customisations_selected: customisationsSelected,
                        },
                      })

                      setTooltipKey(null)
                      onSelectRecipe(value.recipeId as RecipeUUID)
                    }}
                    isSoldOut={isSoldOut || isUnavailable}
                  >
                    <PillLabel text={value.name} />
                  </SelectablePill>
                  {tooltipKey === valueKey && isUnavailable && (
                    <Box
                      position={DisplayPosition.Absolute}
                      top="100%"
                      left="50%"
                      zIndex={ZIndex.Top}
                      minWidth="180px"
                      maxWidth="300px"
                      width="auto"
                      style={{
                        marginTop: '15px',
                        overflowWrap: 'break-word',
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <UnavailableTooltip
                        text="Sorry, this isn’t available with the other options you’ve selected"
                        onClose={() => setTooltipKey(null)}
                      />
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>
      ))}
    </Box>
  )
}
